import React from 'react';
import { FixedSizeList } from 'react-window';

import * as styles from './styles';
import { Button, Tooltip } from '@cobliteam/ui/components';
import { ClickAwayListener } from '@material-ui/core';
import classnames from 'classnames';

import { ContainerDefault, DefaultInput, Dropdown } from '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

import type { CloseOverlayHandler, PlacementType, TriggerTypes } from '../Dropdown';
import { MenuItem } from '../DropdownMenu/MenuItem';
import { CaptionDefault } from '../typography';

export interface FilterMenuDropdownOptionValue<T> {
  value: T;
  label: string;
  disabled?: boolean;
  toolTip?: string;
  icon?: React.ReactNode;
}
interface FilterMenuDropdownOptionNone {
  value: 'none';
  label: string;
  disabled?: boolean;
  toolTip?: string;
  icon?: React.ReactNode;
}

type FilterMenuDropdownOption<T> = FilterMenuDropdownOptionValue<T> | FilterMenuDropdownOptionNone;

export interface FilterMenuDropdownProps<T> {
  buttonTitle: string;
  disabled?: boolean;
  searchBoxTitle?: string;
  items: Array<FilterMenuDropdownOption<T>>;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  selected?: FilterMenuDropdownOption<T>;
  placement?: PlacementType;
  onSearchBoxInputChange?: (searchText: string) => void;
  onItemClick: (itemValue?: T) => void;
  trigger?: TriggerTypes;
  overflowClassName?: string;
  loading?: boolean;
  name?: string;
  triggerWrapperClass?: string;
  disablePortal?: boolean;
  searchBoxEmptyLabel?: string;
  keepOpen?: boolean;
}

interface FilterMenuDropdownState<T> {
  hovered: boolean;
  selectedItem: FilterMenuDropdownOption<T>;
  searchBoxInput: string;
  buttonTitle: string;
}

const NO_SELECTION_VALUE = 'none';
const NO_SELECTION_ITEM: FilterMenuDropdownOptionNone = {
  value: NO_SELECTION_VALUE,
  label: '— Nenhum —',
};

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class FilterMenuDropdown<T> extends React.Component<FilterMenuDropdownProps<T>, FilterMenuDropdownState<T>> {
  private searchDebounceRef: any = null;

  constructor(props: FilterMenuDropdownProps<T>) {
    super(props);

    this.state = {
      hovered: false,
      selectedItem: this.props.selected || NO_SELECTION_ITEM,
      searchBoxInput: '',
      buttonTitle: this.getButtonTitle(this.props.selected) || this.props.buttonTitle,
    };
  }

  private closeOverlayHandler: CloseOverlayHandler = () => {};

  public componentDidUpdate(prevProps: FilterMenuDropdownProps<T>) {
    if (prevProps.selected !== this.props.selected) {
      this.setState({
        selectedItem: this.props.selected || NO_SELECTION_ITEM,
        buttonTitle: this.getButtonTitle(this.props.selected) || this.props.buttonTitle,
      });
    }
  }

  private searchDebounce = (fn: () => void, wait: number) => {
    if (this.searchDebounceRef) {
      clearTimeout(this.searchDebounceRef);
    }
    this.searchDebounceRef = setTimeout(fn, wait);
  };

  private getButtonTitle = (selected: FilterMenuDropdownOption<T> = NO_SELECTION_ITEM) => {
    const { value, label } = selected;
    if (value !== NO_SELECTION_VALUE && label) {
      return label;
    }

    return this.props.buttonTitle;
  };

  private onHoverChange = (hovered: boolean) => {
    this.setState({
      hovered,
      searchBoxInput: '',
    });
  };

  private onItemClick = (selected: FilterMenuDropdownOption<T>) => {
    const selectedItem = selected;
    const buttonTitle = this.getButtonTitle(selectedItem);

    this.setState({ selectedItem, buttonTitle, hovered: false, searchBoxInput: '' }, () => {
      this.closeOverlayHandler();
      this.props.onItemClick(selectedItem.value !== NO_SELECTION_VALUE ? selectedItem.value : undefined);
    });
  };

  private onSearchBoxInputChange = (searchText: string) => {
    this.setState({
      searchBoxInput: searchText,
    });
  };

  private handleSearchBoxInputChange = ({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    this.searchDebounce(() => {
      this.onSearchBoxInputChange(value);
    }, 400);
  };

  private renderItem = ({ index, style }: { index: number; style: any }) => {
    const items = this.filterItems();
    const item = items[index];
    const isSelected = item.value === this.state.selectedItem.value;
    const isSelectNone = item.value === NO_SELECTION_VALUE;

    const itemLabel = (
      <span>
        {!isSelectNone && item.icon ? item.icon : null}
        {item.label}
      </span>
    );

    return (
      <div style={style}>
        <MenuItem
          key={item.value as string}
          title={item.toolTip ? undefined : item.label}
          onClick={item.disabled ? undefined : () => this.onItemClick(item)}
          className={classnames(styles.item, {
            selectNone: isSelectNone,
            selected: isSelected,
            disabled: item.disabled,
          })}
        >
          <Tooltip enable={!!item.toolTip} content={item.toolTip}>
            {itemLabel}
          </Tooltip>

          {!isSelectNone && isSelected && <Icon name='check' className={styles.checkIcon} />}
        </MenuItem>
      </div>
    );
  };

  private filterItems = () => {
    const { items } = this.props;
    const searchBoxInput = this.state.searchBoxInput.trim().toLowerCase();

    if (searchBoxInput.length > 0) {
      return items.filter(({ label }) => label?.toLocaleLowerCase().match(searchBoxInput));
    }

    return [NO_SELECTION_ITEM, ...items];
  };

  private renderItems = () => {
    const items = this.filterItems();
    const label = this.props.searchBoxEmptyLabel || 'Nenhum veículo encontrado.';

    if (items.length > 0) {
      return (
        <FixedSizeList itemCount={items.length} height={195} itemSize={40} width='100%'>
          {this.renderItem}
        </FixedSizeList>
      );
    }

    return <CaptionDefault className={styles.noResults}>{label}</CaptionDefault>;
  };

  private getOverlay = (closeOverlayHandler: CloseOverlayHandler) => {
    this.closeOverlayHandler = closeOverlayHandler;

    return (
      <ClickAwayListener onClickAway={closeOverlayHandler} mouseEvent='onClick'>
        <ContainerDefault className={classnames(styles.overlay)}>
          <DefaultInput
            startAdornment={<Icon name='search' />}
            placeholder={this.props.searchBoxTitle || 'Buscar'}
            onChange={this.handleSearchBoxInputChange}
            className={styles.searchBox}
            name={this.props.name}
          />
          <div className={styles.itemsList}>{this.renderItems()}</div>
        </ContainerDefault>
      </ClickAwayListener>
    );
  };

  public render() {
    return (
      <Dropdown
        trigger={this.props.trigger}
        overlay={this.getOverlay}
        onHoverChange={this.onHoverChange}
        placement={this.props.placement}
        disablePortal={!!this.props.disablePortal}
        disabled={this.props.disabled || this.props.loading}
        keepOpen={this.props.keepOpen || this.props.keepOpen}
        triggerWrapperClass={this.props.triggerWrapperClass}
      >
        <Button variant='text' startIcon={<Icon name='filter' />} loading={this.props.loading} disabled={this.props.disabled || this.props.loading} size='condensed'>
          {this.state.buttonTitle}
        </Button>
      </Dropdown>
    );
  }
}
